<template>
  <div class="verification-root">
    <header class="top-nav">
      <button class="back-btn" @click="goBack"><</button>
      <div class="title">人脸识别</div>
    </header>

    <!-- 摄像头错误弹窗 -->
    <div v-if="showCameraErrorPopup" class="error-popup-overlay" @click="closeErrorPopup">
      <div class="error-popup-content" @click.stop>
        <img 
          src="@/assets/withdraws/popup3.png" 
          alt="摄像头错误提示" 
          class="error-popup-img"
          @click="goToAuthIdCard"
        />
      </div>
    </div>

    <div class="camera-area" v-if="streaming">
      <video id="inputVideo" autoplay muted playsinline></video>
      <canvas id="overlay"></canvas>
    </div>
    
    <div class="text-worl">
      <div v-if="showFake" class="fake-box-inline">
        <input class="fake-status-input" v-model="currentFakeMessage" />
        <div class="fake-progress">
          <span v-for="n in fakeMessages.length" :key="n" class="dot" :class="{active: n-1 <= fakeIndex}"></span>
        </div>
        <div style="height:8px"></div>
        <button v-if="showCompleteButton" class="complete-btn" @click="onFakeComplete">完成</button>
      </div>
    </div>
    
    <div class="verification-container" v-if="!streaming && !showCameraErrorPopup">
      <div class="face-box">
        <img src="@/assets/verification/人脸.png" alt="人脸框1" class="face-img" />
      </div>
      <div class="text-box">
        <img src="@/assets/verification/文字.png" alt="人脸框2" style="width: 80%" />
      </div>
      <div class="text-box" @click="startCamera">
        <img
          src="@/assets/verification/点击进行人脸识别.png"
          alt="人脸框3"
          class="face-img"
        />
      </div>
      <div class="text-box" @click="$router.push('/authIdCard')">
        <img
          src="@/assets/verification/跳转身份证链接.png"
          alt="人脸框4"
          class="face-img"
        />
      </div>
    </div>
  </div>
</template>

<script>
  import * as faceapi from "face-api.js";

  export default {
    name: 'Verification',
    data() {
      return {
        streaming: false,
        modelsLoaded: false,
        stream: null,
        detectionInterval: null,
        // fake recognition state
        showFake: false,
        fakeMessages: [
          '正在扫描...',
          '检查面部特征...',
          '分析面部轮廓...',
          '验证身份信息...'
        ],
        fakeIndex: -1,
        fakeTimer: null,
        // 可双向绑定的当前显示文本（用于 v-model / contenteditable）
        currentFakeMessage: '',
        showCompleteButton: false,
        // 新增：摄像头错误弹窗控制
        showCameraErrorPopup: false,
      };
    },
    created() {
      this.$parent.footer('user', false);
    },
    methods: {
      goBack() {
        this.$router.go(-1);
      },
      async loadModels() {
        if (this.modelsLoaded) return;

        const MODEL_URL = "/models";
        await Promise.all([
          faceapi.nets.tinyFaceDetector.loadFromUri(MODEL_URL),
          faceapi.nets.faceLandmark68Net.loadFromUri(MODEL_URL),
        ]);
        this.modelsLoaded = true;
      },
      async startCamera() {
        await this.loadModels();
        if (this.streaming) return;
        
        try {
          const constraints = { video: { facingMode: "user" }, audio: false };
          this.stream = await navigator.mediaDevices.getUserMedia(constraints);
          const video = document.getElementById("inputVideo");
          video.srcObject = this.stream;
          await video.play();
          
          // 成功打开摄像头
          this.streaming = true;
          this.showCameraErrorPopup = false; // 确保错误弹窗关闭
          await this.$nextTick();
          
          // 权限获取并开始播放后，启动假识别流程
          this.startFakeRecognition();
        } catch (err) {
          console.error("无法打开摄像头", err);
          // 显示摄像头错误弹窗
          this.showCameraErrorPopup = true;
          
          // 可选：保留原来的toast提示
          this.$toast && this.$toast({ message: "请允许摄像头权限" });
        }
      },
      stopCamera() {
        this.streaming = false;
        if (this.detectionInterval) {
          clearTimeout(this.detectionInterval);
          this.detectionInterval = null;
        }
        // 停止假识别
        if (this.fakeTimer) {
          clearTimeout(this.fakeTimer);
          this.fakeTimer = null;
        }
        this.showFake = false;
        this.fakeIndex = -1;
        this.showCompleteButton = false;
        if (this.stream) {
          this.stream.getTracks().forEach((t) => t.stop());
          this.stream = null;
        }
        const canvas = document.getElementById("overlay");
        const ctx = canvas && canvas.getContext && canvas.getContext("2d");
        if (ctx) ctx.clearRect(0, 0, canvas.width, canvas.height);
      },
      async startFakeRecognition() {
        if (this.showFake) return;
        this.showFake = true;
        this.fakeIndex = -1;
        this.showCompleteButton = false;
        const sleep = (ms) => new Promise((res) => setTimeout(res, ms));
        for (let i = 0; i < this.fakeMessages.length; i++) {
          this.fakeIndex = i;
          this.currentFakeMessage = this.fakeMessages[i];
          // 等待 1 秒或直到面板被关闭
          await sleep(2000);
          if (!this.showFake) return;
        }
        // 所有步骤完成
        this.$toast({ message: "认证完成" });
        this.showCompleteButton = true;
      },
      onFakeComplete() {
        this.showFake = false;
        this.fakeIndex = -1;
        this.showCompleteButton = false;
        this.$router.push('/authAccount');
      },
      // 新增：关闭错误弹窗
      closeErrorPopup() {
        this.showCameraErrorPopup = false;
      },
      goToAuthIdCard() {
        this.showCameraErrorPopup = false;
        this.$router.push('/authIdCard');
      },
      async onPlay() {
        const video = document.getElementById("inputVideo");
        const canvas = document.getElementById("overlay");
        const displaySize = {
          width: video.videoWidth,
          height: video.videoHeight,
        };
        faceapi.matchDimensions(canvas, displaySize);

        const options = new faceapi.TinyFaceDetectorOptions({
          inputSize: 224,
          scoreThreshold: 0.5,
        });

        const detect = async () => {
          if (!this.streaming) return;
          const detections = await faceapi
            .detectAllFaces(video, options)
            .withFaceLandmarks();
          const resized = faceapi.resizeResults(detections, displaySize);
          const ctx = canvas.getContext("2d");
          ctx.clearRect(0, 0, canvas.width, canvas.height);
          faceapi.draw.drawDetections(canvas, resized);
          faceapi.draw.drawFaceLandmarks(canvas, resized);
          this.detectionInterval = setTimeout(detect, 100);
        };

        canvas.width = displaySize.width;
        canvas.height = displaySize.height;
        detect();
      },
      capture() {
        const video = document.getElementById("inputVideo");
        const canvas = document.createElement("canvas");
        canvas.width = video.videoWidth;
        canvas.height = video.videoHeight;
        const ctx = canvas.getContext("2d");
        ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
        const dataUrl = canvas.toDataURL("image/png");
        console.log("capture", dataUrl);
      },
    },
  };
</script>

<style lang="less" scoped>
  .verification-root {
    background-color: #ffffff;
    min-height: 100vh;
    box-sizing: border-box;
    color: #222;
    position: relative;
  }
  
  .top-nav {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    height: 56px;
    display: flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    z-index: 20;
  }
  
  .back-btn {
    position: absolute;
    left: 12px;
    top: 50%;
    transform: translateY(-50%);
    background: transparent;
    border: none;
    font-size: 20px;
    cursor: pointer;
    color: #bdb4b4;
  }
  
  .top-nav .title {
    font-size: 16px;
    font-weight: 600;
    color: #bdb4b4;
  }
  
  .face-img{
    display: block;
    max-width: 90%;
    max-height: 90%;
    -o-object-fit: contain;
    object-fit: contain;
  }
  
  .verification-container {
    padding-top: 76px;
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: flex-start;
    gap: 20px;
    padding-bottom: 40px;
  }
  
  .face-box {
    width: 220px;
    height: 220px;
  }
  
  .text-box {
    width: 80%;
    height: auto;
    display: flex;
    align-items: center;
    justify-content: center;
  }
  
  .camera-area {
    position: relative;
    width: 320px;
    max-width: 90%;
    margin: 80px auto 10px;
  }
  
  .text-worl { 
    display:flex; 
    justify-content:center; 
    margin-top:12px; 
  }
  
  .text-worl .fake-box-inline { 
    width:100%; 
    max-width:420px; 
    text-align:center; 
  }
  
  .text-worl .fake-status-input {
    width: 100%;
    border: none;
    background: transparent;
    text-align: center;
    font-size: 16px;
    color: #333;
    margin-bottom: 8px;
    outline: none;
  }
  
  .text-worl .fake-progress { 
    display:flex; 
    gap:8px; 
    justify-content:center; 
    margin-bottom:8px; 
  }
  
  .dot { 
    width:10px; 
    height:10px; 
    border-radius:50%; 
    background:#eee; 
    display:inline-block; 
  }
  
  .dot.active { 
    background:#007bff; 
  }
  
  .complete-btn {
    background: #007bff;
    color: #fff;
    border: none;
    width: 80%;
    max-width: 360px;
    height: 44px;
    padding: 0 12px;
    border-radius: 22px;
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    font-weight: 600;
    font-size: 15px;
    box-shadow: 0 2px 6px rgba(0, 123, 255, 0.2);
  }

  button {
    margin: 0 5px;
    padding: 10px 20px;
    font-size: 16px;
    cursor: pointer;
  }

  /* 新增：摄像头错误弹窗样式 */
  .error-popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-color: rgba(0, 0, 0, 0.7);
    display: flex;
    align-items: center;
    justify-content: center;
    z-index: 1000;
    animation: fadeIn 0.3s ease;
  }

  .error-popup-content {
    max-width: 90%;
    max-height: 90%;
    display: flex;
    align-items: center;
    justify-content: center;
  }

  .error-popup-img {
    width: 100%;
    height: auto;
    max-width: 300px;
    border-radius: 8px;
    cursor: pointer;
    transition: transform 0.2s ease;
  }

  .error-popup-img:hover {
    transform: scale(1.02);
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
</style>